import type { CatalogCard, SearchResponse } from "@pokifolio/contracts";
import { useEffect, useRef, useState } from "react";

import { PageHeader } from "../../components/page-header";
import { ErrorAlert } from "../../components/ui/alert";
import { CardPicker } from "./card-picker";
import { ResultsSkeleton } from "./results-skeleton";
import { type SearchTarget, SearchedCardBar } from "./searched-card-bar";
import { useLastSearch, useRunSearch } from "./search-queries";
import { SearchResults } from "./search-results";

/** What a finished search was for, so the bar can show it again after coming back to the page. */
function targetOf(results: SearchResponse): SearchTarget {
  return results.card === null
    ? { kind: "words", words: results.query }
    : { kind: "card", card: results.card };
}

/**
 * Find every listing of one card: pick the card, then see results from eBay and the web.
 * Once a card is picked, the grid of matching cards folds into a bar above the results (D-61).
 * `initialWords` (from the quick search) are searched straight away.
 */
export function SearchPage({ initialWords }: { initialWords?: string | undefined }) {
  const search = useRunSearch();
  const lastSearch = useLastSearch();
  const results = search.data ?? (search.isIdle ? lastSearch : undefined);
  const [target, setTarget] = useState<SearchTarget | null>(null);
  const [matchesOpen, setMatchesOpen] = useState(true);
  const shown = target ?? (results === undefined ? null : targetOf(results));

  const run = (next: SearchTarget) => {
    setTarget(next);
    setMatchesOpen(false);
    search.mutate(next.kind === "card" ? { cardId: next.card.id } : { query: next.words });
  };
  const pickCard = (picked: CatalogCard) => run({ kind: "card", card: picked });
  const searchWords = (words: string) => run({ kind: "words", words });

  const started = useRef(false);
  const { mutate } = search;
  useEffect(() => {
    if (initialWords === undefined || started.current) return;
    started.current = true;
    setTarget({ kind: "words", words: initialWords });
    setMatchesOpen(false);
    mutate({ query: initialWords });
  }, [initialWords, mutate]);

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Search"
        description="Find a card on eBay and other websites. Every search is saved."
      />

      <CardPicker
        onPickCard={pickCard}
        onSearchWords={searchWords}
        busy={search.isPending}
        initialText={initialWords ?? ""}
        showMatches={matchesOpen || shown === null}
        onTyping={() => setMatchesOpen(true)}
      />

      {shown !== null && (
        <SearchedCardBar
          target={shown}
          searching={search.isPending}
          onSearchAgain={() => run(shown)}
          onChangeCard={() => {
            setMatchesOpen(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
        />
      )}

      <div aria-live="polite" className="flex flex-col gap-6">
        {search.isPending ? (
          <ResultsSkeleton />
        ) : search.isError ? (
          <ErrorAlert error={search.error} />
        ) : results === undefined ? null : (
          <SearchResults key={results.searchId} search={results} />
        )}
      </div>
    </div>
  );
}
